export { developmentLandingStockMinimum } from "./development-landing-stock-policy.mjs";
import { COUNTRIES, type Country } from "./data";
export { listingDistanceUnit, listingMileageUnits } from "./distance-policy";

export { COUNTRIES };
export type { Country };
export {
  MARKET_LOCALE_REGISTRY,
  countryCodeToMarketLocale,
  marketLocaleForPath,
} from "./market-locales";
export type { MarketLocale } from "./market-locales";
export {
  REVIEWED_LANDING_INTENT_OVERRIDES,
  applyReviewedLandingIntentOverride,
  reviewedLandingIntentOverride,
} from "./landing-intent-overrides";
export type {
  LandingIntentOverrideTarget,
  ReviewedLandingIntentOverride,
} from "./landing-intent-overrides";
export {
  HOLD_CURRENCIES,
  PARTIAL_PAY_CAPTURE_CURRENCIES,
  isHoldCurrency,
  isPartialPayCaptureCurrency,
} from "./payment-policy";
export type { HoldCurrency, PartialPayCaptureCurrency } from "./payment-policy";

const BY_CODE: Record<string, Country> = {};
const NAME_TO_CODE: Record<string, string> = {};
const COUNTRY_NAMES_MAP: Record<string, string> = {};
const COUNTRY_FLAGS_MAP: Record<string, string> = {};
const COUNTRY_CURRENCIES_MAP: Record<string, string> = {};

for (const c of COUNTRIES) {
  BY_CODE[c.code] = c;
  NAME_TO_CODE[c.name.toLowerCase()] = c.code;
  COUNTRY_NAMES_MAP[c.code] = c.name;
  if (c.flag) COUNTRY_FLAGS_MAP[c.code] = c.flag;
  if (c.currency) COUNTRY_CURRENCIES_MAP[c.code] = c.currency;
}

/** Map of ISO-3166-1 alpha-2 code -> English name. */
export const COUNTRY_NAMES: Readonly<Record<string, string>> = COUNTRY_NAMES_MAP;
/** Map of ISO-3166-1 alpha-2 code -> emoji flag (only for countries with one defined). */
export const COUNTRY_FLAGS: Readonly<Record<string, string>> = COUNTRY_FLAGS_MAP;
/** Map of ISO-3166-1 alpha-2 code -> default ISO-4217 currency (only where defined). */
export const COUNTRY_CURRENCIES: Readonly<Record<string, string>> = COUNTRY_CURRENCIES_MAP;

export function getCountry(code: string): Country | undefined {
  return BY_CODE[code.trim().toUpperCase()];
}

export function countryCodeToName(code: string): string | undefined {
  return COUNTRY_NAMES_MAP[code.trim().toUpperCase()];
}

export function countryNameToCode(name: string): string | undefined {
  return NAME_TO_CODE[name.trim().toLowerCase()];
}

export function countryCodeToFlag(code: string): string | undefined {
  return COUNTRY_FLAGS_MAP[code.trim().toUpperCase()];
}

export function isIsoCountryCode(value: string): boolean {
  const trimmed = value.trim();
  return /^[A-Za-z]{2}$/.test(trimmed) && BY_CODE[trimmed.toUpperCase()] !== undefined;
}

/**
 * Task #2608 — canonical ISO-3166-1 alpha-2 codes for every country
 * the BookAclassic platform has launched in. This is the single
 * source of truth for "countries we are live in" and gates:
 *
 *   - the admin Countries grid (`/admin/countries`)
 *   - the car-listing wizard's country dropdown (`SubStepLocation`)
 *   - the payout-policy bootstrap reseed (which used to fan rows
 *     across every Stripe-supported country, polluting the admin
 *     grid with phantoms like SG / SM / JP / HK / MY / MX)
 *
 * Kept in lock-step with the `MARKETS` array in
 * `artifacts/bookaclassic/src/i18n/markets.ts` via the unit test
 * `active-market-countries.drift.test.ts`. Update both lists
 * together when onboarding a new launch market.
 */
export const ACTIVE_MARKET_COUNTRIES: readonly string[] = [
  "GB", "IE", "US", "CA", "AU", "NZ", "ZA",
  "DE", "FR", "ES", "IT", "PT", "DK", "SE", "MT",
  // Already-approved Web markets; keep shared consumers aligned with MARKETS.
  "AT", "CZ", "IN",
  // Owner-approved partial-payment markets; financial activation is separate.
  "CH", "AR", "LT", "LV",
  // 11 high-inventory launch markets added on the new stack (BE/NL re-enabled).
  "BR", "PL", "SK", "NO", "BG", "CO", "FI", "NL", "BE", "EC", "MX",
];

const ACTIVE_MARKET_COUNTRIES_SET: ReadonlySet<string> = new Set(ACTIVE_MARKET_COUNTRIES);

export function isActiveMarketCountry(code: string | null | undefined): boolean {
  if (!code) return false;
  return ACTIVE_MARKET_COUNTRIES_SET.has(code.trim().toUpperCase());
}
